import React from "react";
import useDarkMode from "../../../../hooks/useDarkMode.js";
import useThemeColor from "../../../../hooks/useThemeColor.js";

/**
 * Staff-style header actions: Cancel (exit) + Save (stay) + Save and Exit.
 * Cancel navigates away; unsaved-changes guard still prompts when the form is dirty.
 */
export default function SaveActions({
  onSave,
  onSaveAndExit,
  onCancel,
  saving = false,
  disabled = false,
  saveLabel = "Save",
  saveAndExitLabel = "Save and Exit",
  cancelLabel = "Cancel",
  savingLabel = "Saving...",
  showCancel = true,
}) {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const isDisabled = saving || disabled;
  const showCancelBtn = showCancel && typeof onCancel === "function";

  const cancelStyle = {
    background: "#dc3545",
    border: "none",
    color: "#fff",
    borderRadius: "8px",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "600",
    transition: "all 0.3s ease",
    boxShadow: "0 2px 8px rgba(220, 53, 69, 0.35)",
    cursor: isDisabled ? "not-allowed" : "pointer",
  };

  const secondaryStyle = {
    background: isDarkMode ? "#495057" : "#6c757d",
    border: "none",
    color: "white",
    borderRadius: "8px",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "600",
    transition: "all 0.3s ease",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    cursor: isDisabled ? "not-allowed" : "pointer",
  };

  const primaryStyle = {
    backgroundColor: themeColor,
    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
    border: "none",
    color: "white",
    borderRadius: "8px",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "600",
    transition: "all 0.3s ease",
    boxShadow: `0 2px 8px ${themeColor}40`,
    cursor: isDisabled ? "not-allowed" : "pointer",
  };

  return (
    <div className="ms-auto d-flex align-items-center gap-2 flex-wrap justify-content-end">
      {showCancelBtn && (
        <button
          type="button"
          onClick={onCancel}
          disabled={isDisabled}
          className="btn px-4"
          style={cancelStyle}
          title="Leave without saving"
          onMouseEnter={(e) => {
            if (!isDisabled) {
              e.currentTarget.style.backgroundColor = "#c82333";
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(220, 53, 69, 0.45)";
            }
          }}
          onMouseLeave={(e) => {
            if (!isDisabled) {
              e.currentTarget.style.backgroundColor = "#dc3545";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(220, 53, 69, 0.35)";
            }
          }}
        >
          {cancelLabel}
        </button>
      )}
      <button
        type="button"
        onClick={onSave}
        disabled={isDisabled}
        className="btn px-4"
        style={secondaryStyle}
        onMouseEnter={(e) => {
          if (!isDisabled) {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.2)";
          }
        }}
        onMouseLeave={(e) => {
          if (!isDisabled) {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
          }
        }}
      >
        <i className="fa fa-save fa-lg me-2 ms-n2 text-white" />
        {saving ? savingLabel : saveLabel}
      </button>
      <button
        type="button"
        onClick={onSaveAndExit}
        disabled={isDisabled}
        className="btn px-4"
        style={primaryStyle}
        onMouseEnter={(e) => {
          if (!isDisabled) {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = `0 4px 12px ${themeColor}60`;
          }
        }}
        onMouseLeave={(e) => {
          if (!isDisabled) {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = `0 2px 8px ${themeColor}40`;
          }
        }}
      >
        <i className="fa fa-save fa-lg me-2 ms-n2 text-white" />
        {saving ? savingLabel : saveAndExitLabel}
      </button>
    </div>
  );
}
